import { forwardRef } from "react";
import { Sparkles } from "lucide-react";

type Props = {
  handle: string;
  firstName?: string | null;
  url: string;
};

/**
 * Branded QR poster — printable for schools, events, classrooms.
 * Rendered at 1080×1350 (Instagram portrait) and exported via html-to-image.
 */
const QrPoster = forwardRef<HTMLDivElement, Props>(function QrPoster(
  { handle, firstName, url },
  ref,
) {
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=640x640&margin=2&data=${encodeURIComponent(url)}`;
  const greet = firstName?.trim() ? firstName.trim() : "A Wekiteer";

  return (
    <div
      ref={ref}
      style={{ width: 1080, height: 1350 }}
      className="relative overflow-hidden font-display text-white"
    >
      {/* Gradient backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 20% 0%, #6366f1 0%, #4338ca 35%, #1e1b4b 75%, #0b0a1f 100%)",
        }}
      />
      {/* Soft glow */}
      <div
        className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(236,72,153,0.45), rgba(236,72,153,0))",
        }}
      />

      <div className="relative h-full flex flex-col p-20">
        <div className="flex items-center gap-3 text-white/90 text-2xl tracking-tight">
          <Sparkles className="h-7 w-7" />
          <span style={{ fontWeight: 600 }}>WeKIT™</span>
          <span className="text-white/50">·</span>
          <span className="text-white/70 text-xl">WeKIT™ Career Clarity 360</span>
        </div>

        <div className="mt-16">
          <p className="text-xl tracking-widest uppercase text-white/60">
            {greet} invited you
          </p>
          <h1
            className="mt-6 text-[88px] leading-[0.95] tracking-tight"
            style={{ fontWeight: 800 }}
          >
            Discover the
            <br />
            career you were
            <br />
            <span
              style={{
                background:
                  "linear-gradient(90deg, #f0abfc, #fde68a, #a5f3fc)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              meant for.
            </span>
          </h1>
          <p className="mt-8 text-2xl text-white/80 leading-snug max-w-[640px]">
            30–60 minutes. 5,000+ careers of the future. One personal map to your future of work.
          </p>
        </div>

        <div className="mt-auto flex items-end justify-between gap-12">
          <div className="flex-1">
            <div className="inline-flex items-center gap-3 rounded-full bg-white/10 backdrop-blur px-6 py-3 text-2xl">
              <span style={{ fontWeight: 700 }}>₹100 OFF</span>
              <span className="text-white/70 text-xl">with this link</span>
            </div>
            <p className="mt-8 text-2xl text-white/70">Scan or visit</p>
            <p
              className="mt-2 text-3xl"
              style={{ fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              wekit.ai/ref/{handle}
            </p>
          </div>
          <div className="rounded-[32px] bg-white p-6 shadow-2xl shrink-0">
            <img
              src={qr}
              width={320}
              height={320}
              alt=""
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default QrPoster;
